import {
  TableCellProps,
  WbBadge,
  WbButton,
  WbComponentsListArray,
  WbMoreOptions,
  WbTable,
  WbResponsiveTableToolbar,
  createNameWithVersionMap,
  createActionsColumn,
} from '@agilelab/plugin-wb-platform';
import { RoleAssignments } from '@agilelab/plugin-wb-rbac-common';
import { ErrorPanel } from '@backstage/core-components';
import {
  Box,
  ListItemIcon,
  makeStyles,
  MenuItem,
  Typography,
} from '@material-ui/core';
import AccountBoxIcon from '@material-ui/icons/AccountBox';
import CancelIcon from '@material-ui/icons/Cancel';
import EditIcon from '@material-ui/icons/Edit';
import GroupIcon from '@material-ui/icons/Group';
import PersonIcon from '@material-ui/icons/Person';
import TuneIcon from '@material-ui/icons/Tune';
import { FC, isValidElement, ReactNode, useMemo } from 'react';
import { useAssignmentsContext } from './AssignmentsContext';
import { getLabelAndValue } from './utils';
import { Entity } from '@backstage/catalog-model';

const useStyles = makeStyles(
  theme => ({
    header: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    flexBox: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    disabledBadge: {
      backgroundColor: theme.palette.grey[300],
    },
  }),
  { name: 'AssignmentsTable' },
);

interface AssignmentsTableProps {}

const AssignmentsActions: FC = () => {
  const {
    selectedRemoveItems,
    setOpenConfirmDialog,
    setOpenManageDrawer,
    canEditRolePermission,
  } = useAssignmentsContext();

  return (
    <>
      {canEditRolePermission &&
        (!!selectedRemoveItems?.length ? (
          <WbButton
            label="Remove selected"
            startIcon={<CancelIcon />}
            onClick={() => setOpenConfirmDialog(true)}
            color="secondary"
            size="small"
            variant="outlined"
          />
        ) : (
          <WbButton
            label="Add Assignments"
            startIcon={<AccountBoxIcon />}
            onClick={() => {
              setOpenManageDrawer(true);
            }}
            color="primary"
            size="small"
          />
        ))}
    </>
  );
};

export const AssignmentsCardHeader: FC = () => {
  const {
    selectedRemoveItems,
    setSelectedRemoveItems,
    filters,
    setFilters,
    setPagination,
  } = useAssignmentsContext();

  return (
    <WbResponsiveTableToolbar
      searchProps={{
        value: filters.text,
        onChange: search => {
          setFilters({ text: search });
          setPagination(p => ({ ...p, offset: 0 }));
        },
      }}
      selectedProps={{
        selectedCount: selectedRemoveItems?.length ?? 0,
        selectedLabel: 'Assignment',
        onClearSelection: () => setSelectedRemoveItems([]),
      }}
      onClear={() => setFilters({})}
      marginBottom
    >
      <WbResponsiveTableToolbar.Actions>
        <AssignmentsActions />
      </WbResponsiveTableToolbar.Actions>
    </WbResponsiveTableToolbar>
  );
};

const Actions = ({ row }: { row: RoleAssignments }) => {
  const {
    setOpenManageDrawer,
    setOpenConfirmDialog,
    setEditEnabledScopesDrawer,
    setSelectedRow,
    selectedRow,
  } = useAssignmentsContext();

  const onMenuClose = () => {
    setSelectedRow(undefined);
  };

  return (
    <WbMoreOptions
      open={selectedRow?.userOrGroup?.urn === row.userOrGroup?.urn}
      IconProps={{ style: { fontSize: 16 } }}
      MenuProps={{ onClick: e => e.stopPropagation() }}
      onClick={e => {
        e.stopPropagation();
        setSelectedRow(row);
      }}
      onClose={onMenuClose}
    >
      <MenuItem
        onClick={() => {
          setOpenManageDrawer(true);
        }}
      >
        <ListItemIcon>
          <EditIcon fontSize="small" />
        </ListItemIcon>
        Edit scope
      </MenuItem>
      {row.scopes?.[0]?.urn !== '*' && (
        <MenuItem
          onClick={() => {
            setEditEnabledScopesDrawer(true);
          }}
        >
          <ListItemIcon>
            <TuneIcon fontSize="small" />
          </ListItemIcon>
          Enable/Disable Scope
        </MenuItem>
      )}
      <MenuItem
        onClick={() => {
          setOpenConfirmDialog(true);
        }}
      >
        <ListItemIcon>
          <CancelIcon fontSize="small" />
        </ListItemIcon>
        Remove assignments
      </MenuItem>
    </WbMoreOptions>
  );
};

export const AssignmentsTable: FC<AssignmentsTableProps> = () => {
  const {
    selectedRemoveItems,
    setSelectedRemoveItems,
    assignmentsLoaded,
    canEditRolePermission,
    pagination,
    setPagination,
    totalAssignmentsCount,
    filteredAssignments,
  } = useAssignmentsContext();

  const classes = useStyles();

  const paginatedAssignments = useMemo(() => {
    return filteredAssignments.slice(
      pagination.offset,
      pagination.offset + pagination.limit,
    );
  }, [filteredAssignments, pagination.offset, pagination.limit]);

  const handlePageChange = (page: number) => {
    setPagination({
      ...pagination,
      offset: page * pagination.limit,
    });
  };

  const filterBadgeByQuery = (child: ReactNode, query: string): boolean => {
    if (!isValidElement(child)) return false;

    const childContent = child.props?.children;

    const text =
      typeof childContent === 'string' || typeof childContent === 'number'
        ? String(childContent)
        : '';

    return text.toLowerCase().includes(query.toLowerCase());
  };

  const columns: TableCellProps<RoleAssignments>[] = [
    {
      field: 'userOrGroup',
      headerName: 'user and groups',
      fieldRender: field => (
        <Box className={classes.flexBox}>
          {field.userOrGroup?.entity?.kind === 'User' ? (
            <PersonIcon fontSize="small" />
          ) : (
            <GroupIcon fontSize="small" />
          )}
          <Typography variant="body2" style={{ fontWeight: '500' }}>
            {(field.userOrGroup?.entity?.spec?.profile as any)?.displayName ??
              field.userOrGroup?.entity?.metadata?.name ??
              field.userOrGroup?.urn}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'scopes',
      width: '70%',
      headerName: 'scope',
      fieldRender: field => {
        if (field.scopes[0]?.urn === '*')
          return <WbBadge key="*">*All*</WbBadge>;

        const adaptedValues = field.scopes
          .filter(s => s.entity !== undefined)
          .map(s => ({ ...s, ...getLabelAndValue(s.entity as Entity) }));

        const nameWithVersionMap = createNameWithVersionMap(
          adaptedValues,
          'displayName',
        );

        const children = adaptedValues.map(val => {
          const name = val?.entity
            ? nameWithVersionMap[val.id].nameWithVersion
            : val?.urn;

          return (
            <WbBadge
              key={name}
              className={!val.enabled ? classes.disabledBadge : ''}
            >
              {name}
            </WbBadge>
          );
        });

        return (
          <Box key={`${field.roleId}-scopes`} className={classes.flexBox}>
            {!!field.scopes.length && (
              <WbComponentsListArray
                children={children}
                limit={15}
                filterOnChange={filterBadgeByQuery}
              />
            )}
          </Box>
        );
      },
    },
    createActionsColumn({
      renderActions: row => canEditRolePermission && <Actions row={row} />,
    }),
  ];

  if (assignmentsLoaded.error)
    return (
      <ErrorPanel
        error={
          assignmentsLoaded.error instanceof Error
            ? assignmentsLoaded.error
            : new Error('Something went wrong')
        }
      />
    );

  const getRowId = (r: RoleAssignments) => r.userOrGroup?.urn;

  return (
    <WbTable<RoleAssignments>
      selection={canEditRolePermission ? 'multiple' : undefined}
      selectedItems={selectedRemoveItems}
      setSelectedItems={setSelectedRemoveItems}
      styles={{
        container: { flex: '1 1 auto' },
      }}
      components={{
        tableLoader: {
          loading: assignmentsLoaded.loading,
        },
        tableContent: {
          columns,
          rows: paginatedAssignments,
          getRowId,
        },
      }}
      pagination={{
        count: totalAssignmentsCount,
        limit: pagination.limit,
        offset: pagination.offset,
        onPageChange: handlePageChange,
        onRowsPerPageChange: () => {},
        rowsPerPageOptions: [],
      }}
    />
  );
};
